import type { RequestInfo } from '@warp-drive/core/types/request';
import { buildBaseURL, type QueryUrlOptions } from '@warp-drive/utilities';
import { SkipCache } from '@warp-drive/core-types/request';
import type { Store } from '@warp-drive/core';
import type { PslfForm } from '../data/resources/pslf-form.type.ts';

/**
 * PslfForm action request builders.
 *
 * Replaces the EMPLOYEE-shared custom methods on the near-identical
 * `tio-employee/app/adapters/pslf-form.ts` and `tio-admin/app/adapters/pslf-form.ts`.
 *
 * ⚠️ RESOURCE PATH — all of these resolve to the PLURAL `pslf-forms`. The legacy
 * employee adapter built the V1 URLs two ways that converge on the same path:
 * `this.buildURL('pslf-form', id)` (the `JSONAPIAdapter` pluralizes the model
 * name → `/pslf-forms/{id}`) for sign-borrower / sign-employer / cancel / reject /
 * notify-employer-sign-reminder, and `this.buildURL('pslf-forms') + '/{id}/...'`
 * (already plural) for borrower-embed-document / borrower-pre-approved-embed-document /
 * transfer-pdf. Both forms emit `/pslf-forms/...`, so every V1 builder here uses
 * `resourcePath: 'pslf-forms'`. The two V2 methods
 * (`sign-non-partner-employer`, which the adapter built as
 * `${this.host}/api/v2/pslf-forms/sign-non-partner-employer`) use the explicit
 * `api/v2/pslf-forms` path; `non-partner-employer/verify` is V1 (`buildURL('pslf-forms')`).
 *
 * ⚠️ X-Reindex-Mode: sync header — the legacy `triggerFormActionAndReload` set
 * `{ 'X-Reindex-Mode': 'sync' }` ONLY when `options.syncReindex` was true, which
 * is exactly sign-borrower / sign-employer / cancel (and `reject`, which set the
 * header inline). notify-employer-sign-reminder and transfer-pdf went through
 * `triggerFormActionAndReload` with NO syncReindex, so they carry NO custom
 * header — preserved EXACTLY here (the header is set on the RequestInfo `headers`
 * ONLY for the four sync methods). The handler chain merges request-manager
 * defaults via set-if-missing, so a per-request header placed in the builder's
 * `headers` rides through untouched.
 *
 * demoMode: none of these adapter methods consulted `demoMode`, so there is NO
 * demo seam to push to the call site.
 *
 * The legacy state-changing PATCH methods (sign-borrower / sign-employer / cancel /
 * reject / notify-employer-sign-reminder) folded in `pslfForm.reload()` after the
 * `ajax` call; the call sites now reload explicitly after the request (the
 * established wave-1.5 pattern — see `tio-data/requests/observable-document-actions`
 * and the employees/show.gts conversions). `transfer-pdf` did NOT reload in the
 * EMPLOYEE adapter (the admin adapter's transfer-pdf DID — that reload lives on
 * the model wrapper, whose only caller is admin).
 *
 * wave 3 — admin: the admin-only adapter method `sendFax`
 * (PATCH /pslf-forms/{id}/fax, reload) is single-app (admin) surface — its only
 * callers are the tio-admin `app/` templates AND the `sendFax()` wrappers on the
 * shared `models/pslf-form.ts` / `data/resources/pslf-form.ext.ts`, both of which
 * are exercised only by the admin app. It is now converted via
 * `faxPslfFormRequest` below; the model/resource-extension `sendFax()` wrappers
 * route through that builder (the reload lives on the wrapper, matching the admin
 * adapter, whose only caller is admin).
 */

const pslfFormsUrlOptions: QueryUrlOptions = {
  identifier: { type: 'pslf-forms' },
  op: 'query',
  resourcePath: 'pslf-forms',
};

const pslfFormsV2UrlOptions: QueryUrlOptions = {
  identifier: { type: 'pslf-forms' },
  op: 'query',
  resourcePath: 'api/v2/pslf-forms',
};

/** V1 `/pslf-forms/...` */
function pslfFormsUrl(path?: string): string {
  const base = buildBaseURL(pslfFormsUrlOptions);
  return path ? `${base}/${path.replace(/^\/+/, '')}` : base;
}

/** V2 `/api/v2/pslf-forms/...` */
function pslfFormsV2Url(path?: string): string {
  const base = buildBaseURL(pslfFormsV2UrlOptions);
  return path ? `${base}/${path.replace(/^\/+/, '')}` : base;
}

/** The `X-Reindex-Mode: sync` header the sync methods carry. */
function syncReindexHeaders(): Headers {
  const headers = new Headers();
  headers.set('X-Reindex-Mode', 'sync');
  return headers;
}

/**
 * PATCH /pslf-forms/:id/sign-borrower  (header `X-Reindex-Mode: sync`, no body)
 *
 * Mirrors `PslfFormAdapter#signBorrower`, which delegated to
 * `triggerFormActionAndReload(pslfForm, '/sign-borrower', { syncReindex: true })`:
 * `const url = this.buildURL('pslf-form', pslfForm.id) + '/sign-borrower'` then
 * `const headers = { 'X-Reindex-Mode': 'sync' }; await this.ajax(url, 'PATCH', { headers })`
 * then `await pslfForm.reload()`. NO request body. The reload happens at the call
 * site.
 */
export function signBorrowerRequest(id: string): RequestInfo {
  return {
    url: pslfFormsUrl(`${id}/sign-borrower`),
    method: 'PATCH',
    headers: syncReindexHeaders(),
    cacheOptions: { [SkipCache]: true },
  };
}

/**
 * PATCH /pslf-forms/:id/sign-employer  (header `X-Reindex-Mode: sync`, no body)
 *
 * Mirrors `PslfFormAdapter#signEmployer`, which delegated to
 * `triggerFormActionAndReload(pslfForm, '/sign-employer', { syncReindex: true })`
 * (same `ajax(url, 'PATCH', { headers: { 'X-Reindex-Mode': 'sync' } })` shape as
 * sign-borrower, no body). The reload happens at the call site.
 */
export function signEmployerRequest(id: string): RequestInfo {
  return {
    url: pslfFormsUrl(`${id}/sign-employer`),
    method: 'PATCH',
    headers: syncReindexHeaders(),
    cacheOptions: { [SkipCache]: true },
  };
}

/**
 * PATCH /pslf-forms/:id/cancel  (header `X-Reindex-Mode: sync`, no body)
 *
 * Mirrors `PslfFormAdapter#cancel`, which delegated to
 * `triggerFormActionAndReload(pslfForm, '/cancel', { syncReindex: true })`
 * (same `ajax(url, 'PATCH', { headers: { 'X-Reindex-Mode': 'sync' } })` shape, no
 * body). The reload happens at the call site.
 */
export function cancelPslfFormRequest(id: string): RequestInfo {
  return {
    url: pslfFormsUrl(`${id}/cancel`),
    method: 'PATCH',
    headers: syncReindexHeaders(),
    cacheOptions: { [SkipCache]: true },
  };
}

/**
 * PATCH /pslf-forms/:id/reject  (header `X-Reindex-Mode: sync`)
 * body `{ data: { type: 'pslf-forms', id, attributes: { _changelog } } }`
 *
 * Mirrors `PslfFormAdapter#reject`, which built the URL via
 * `this.buildURL('pslf-form', pslfForm.id) + '/reject'` and sent
 * `this.ajax(url, 'PATCH', { headers: { 'X-Reindex-Mode': 'sync' }, data: { data: { type: 'pslf-forms', id: pslfForm.id, attributes: { _changelog: comment } } } })`
 * — note this body carries `type` and `id` at the top of `data` (UNLIKE the
 * employee/tas-participant transitions, which sent only `{ data: { attributes } }`).
 * The `comment` defaults to `''` exactly as the adapter did. Ember Data's `data`
 * option IS the request body, so the wire body is the inner envelope. The reload
 * happens at the call site.
 */
export function rejectPslfFormRequest(id: string, comment = ''): RequestInfo {
  return {
    url: pslfFormsUrl(`${id}/reject`),
    method: 'PATCH',
    headers: syncReindexHeaders(),
    body: JSON.stringify({
      data: {
        type: 'pslf-forms',
        id,
        attributes: {
          _changelog: comment,
        },
      },
    }),
    cacheOptions: { [SkipCache]: true },
  };
}

/**
 * PATCH /pslf-forms/:id/notify-employer-sign-reminder  (NO custom header, no body)
 *
 * Mirrors `PslfFormAdapter#resendEmployerEmail`, which delegated to
 * `triggerFormActionAndReload(pslfForm, '/notify-employer-sign-reminder')` with NO
 * `syncReindex` option, so the adapter set NO `X-Reindex-Mode` header and sent NO
 * body: `await this.ajax(url, 'PATCH', undefined)`. The reload happens at the call
 * site.
 */
export function notifyEmployerSignReminderRequest(id: string): RequestInfo {
  return {
    url: pslfFormsUrl(`${id}/notify-employer-sign-reminder`),
    method: 'PATCH',
    cacheOptions: { [SkipCache]: true },
  };
}

/**
 * PATCH /pslf-forms/:id/transfer-pdf  (NO custom header, no body)
 *
 * Mirrors the EMPLOYEE `PslfFormAdapter#transferPdf`, which built the URL via
 * `this.buildURL('pslf-forms') + '/{id}/transfer-pdf'` and sent
 * `await this.ajax(url, 'PATCH')` (no header, no body, and — unlike the admin
 * variant — NO reload). The admin adapter's `transferPdf` additionally reloaded;
 * that reload lives on the shared model wrapper whose only caller is admin.
 */
export function transferPdfRequest(id: string): RequestInfo {
  return {
    url: pslfFormsUrl(`${id}/transfer-pdf`),
    method: 'PATCH',
    cacheOptions: { [SkipCache]: true },
  };
}

/**
 * PATCH /pslf-forms/:id/fax  (NO custom header, no body)
 *
 * Mirrors the ADMIN `PslfFormAdapter#sendFax`, which built the URL via
 * `this.buildURL(model.constructor.modelName, model.id) + '/fax'` — the model
 * name is `pslf-form`, which the `JSONAPIAdapter` pluralizes to
 * `/pslf-forms/{id}` — and sent `await this.ajax(url, 'PATCH')` (no header, no
 * body) then `await model.reload()`. The reload happens at the call site (the
 * model / resource-extension `sendFax()` wrappers), matching the admin adapter.
 * The tio-employee pslf-form adapter never defined `sendFax`, so this is an
 * admin-only surface with no employee-wire to reconcile.
 */
export function faxPslfFormRequest(id: string): RequestInfo {
  return {
    url: pslfFormsUrl(`${id}/fax`),
    method: 'PATCH',
    cacheOptions: { [SkipCache]: true },
  };
}

/**
 * Replaces `form.sendFax()` / `form.transferPdf()` on the extension — moved out
 * because WarpDrive resource extensions can't participate in Ember DI.
 */
export function sendPslfFax(store: Store, form: PslfForm) {
  return store.request(faxPslfFormRequest(form.id as string));
}

export function transferPslfPdf(store: Store, form: PslfForm) {
  return store.request(transferPdfRequest(form.id as string));
}

/** A SignNow signer descriptor, exactly as the legacy adapter typed it. */
export interface PslfSigner {
  role: string;
  email: string;
  order: number;
}

/**
 * POST /pslf-forms/:id/borrower-embed-document
 * body `{ templateId, signers, currentSigner: 'Borrower', borrowerSsn, useNewEmbedFlow: true }`
 *
 * Mirrors `PslfFormAdapter#prepareForBorrowerSignature`, which built the URL via
 * `this.buildURL('pslf-forms') + '/{id}/borrower-embed-document'` and sent
 * `this.ajax(url, 'POST', { data: { templateId, signers, currentSigner: 'Borrower', borrowerSsn, useNewEmbedFlow: true } })`.
 * The constant `currentSigner: 'Borrower'` and `useNewEmbedFlow: true` are
 * preserved EXACTLY. (No non-admin call site exercises this today; the builder
 * exists for parity completeness of the borrower-embed family.)
 */
export function borrowerEmbedDocumentRequest(
  id: string,
  signers: [PslfSigner, PslfSigner],
  templateId: string,
  borrowerSsn: string
): RequestInfo {
  return {
    url: pslfFormsUrl(`${id}/borrower-embed-document`),
    method: 'POST',
    body: JSON.stringify({
      templateId,
      signers,
      currentSigner: 'Borrower',
      borrowerSsn,
      useNewEmbedFlow: true,
    }),
    cacheOptions: { [SkipCache]: true },
  };
}

/**
 * POST /pslf-forms/:id/borrower-pre-approved-embed-document
 * body `{ templateId, signers, currentSigner: 'Borrower', borrowerSsn, useNewEmbedFlow: true }`
 *
 * Mirrors `PslfFormAdapter#prepareForBorrowerPreApprovedSignature`, which built
 * the URL via `this.buildURL('pslf-forms') + '/{id}/borrower-pre-approved-embed-document'`
 * and sent the identical `{ data: { templateId, signers, currentSigner: 'Borrower', borrowerSsn, useNewEmbedFlow: true } }`
 * body — the only difference from `borrower-embed-document` is the single-element
 * `signers` tuple. (No non-admin call site exercises this today; parity builder.)
 */
export function borrowerPreApprovedEmbedDocumentRequest(
  id: string,
  signers: [PslfSigner],
  templateId: string,
  borrowerSsn: string
): RequestInfo {
  return {
    url: pslfFormsUrl(`${id}/borrower-pre-approved-embed-document`),
    method: 'POST',
    body: JSON.stringify({
      templateId,
      signers,
      currentSigner: 'Borrower',
      borrowerSsn,
      useNewEmbedFlow: true,
    }),
    cacheOptions: { [SkipCache]: true },
  };
}

/**
 * POST /api/v2/pslf-forms/sign-non-partner-employer  (V2, no id)
 * body `{ approvalId, signature, employerFields }`
 *
 * Mirrors `PslfFormAdapter#signNonPartnerEmployer`, which built the URL inline as
 * `${this.host}/api/v2/pslf-forms/sign-non-partner-employer` (NOT `buildURL`, so
 * the `api/v2` namespace is explicit) and sent
 * `this.ajax(url, 'POST', { data: { approvalId, signature, employerFields } })`.
 * The response is raw; the call site discards it.
 */
export function signNonPartnerEmployerRequest(
  approvalId: string,
  signature: string,
  employerFields: Record<string, unknown>
): RequestInfo {
  return {
    url: pslfFormsV2Url('sign-non-partner-employer'),
    method: 'POST',
    body: JSON.stringify({ approvalId, signature, employerFields }),
    cacheOptions: { [SkipCache]: true },
  };
}

/**
 * POST /pslf-forms/non-partner-employer/verify  (V1, no id)
 * body `{ approvalId }`
 *
 * Mirrors `PslfFormAdapter#verifyNonPartnerEmployer`, which built the URL via
 * `this.buildURL('pslf-forms') + '/non-partner-employer/verify'` and sent
 * `this.ajax(url, 'POST', { data: { approvalId } })`. The adapter wrapped the call
 * in try/catch to rethrow friendly error messages; that error mapping is a call-
 * site concern (the request wire is just the POST). The raw response is the
 * verified employer-form info the call site returns from its route model.
 */
export function verifyNonPartnerEmployerRequest(approvalId: string): RequestInfo {
  return {
    url: pslfFormsUrl('non-partner-employer/verify'),
    method: 'POST',
    body: JSON.stringify({ approvalId }),
    cacheOptions: { [SkipCache]: true },
  };
}
